import type { ReactElement } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from '../../ui-components/button/Button';
import { classNames } from '../../utils/tailwind/classNames';

const DEFAULT_INITIAL_NUM_TO_RENDER = 5;

export type ListRenderItem<ItemT> = (info: {
    item: ItemT;
    index: number;
}) => React.ReactElement | null;

export interface ListRevealProps<ItemT> {
    data: ItemT[];
    keyExtractor?: (item: ItemT, index: number) => string;
    renderItem: ListRenderItem<ItemT> | null | undefined;
    initialNumToRender?: number;
    id?: string;
    listContainerClassName?: string;
}

export function ListReveal<ItemT>({
    data,
    keyExtractor,
    renderItem,
    initialNumToRender = DEFAULT_INITIAL_NUM_TO_RENDER,
    id,
    listContainerClassName,
}: ListRevealProps<ItemT>): ReactElement {
    const [renderedCount, setRenderedCount] = useState(initialNumToRender);
    const hasMore = data.length > renderedCount;

    return (
        <div data-testid={`${id ? `${id}-` : ''}list-reveal-container`}>
            <div
                data-testid={`${id ? `${id}-` : ''}list-reveal-items`}
                role="list"
                className={classNames(listContainerClassName)}
            >
                {data.slice(0, renderedCount).map((item, index) => {
                    const key = keyExtractor ? keyExtractor(item, index) : index.toString();
                    return (
                        <div key={key} role="listitem">
                            {renderItem ? renderItem({ item, index }) : null}
                        </div>
                    );
                })}
            </div>
            {hasMore && (
                <div
                    data-testid={`${id ? `${id}-` : ''}list-reveal-footer`}
                    className="mt-4 flex justify-start"
                >
                    <Button
                        variant="text-button-no-hover"
                        onClick={() => setRenderedCount(data.length)}
                        id={`${id ? `${id}-` : ''}list-reveal-show-more-button`}
                    >
                        <FormattedMessage id="list-reveal.show-more" defaultMessage="Show More" />
                    </Button>
                </div>
            )}
        </div>
    );
}

ListReveal.defaultProps = {
    id: undefined,
    keyExtractor: undefined,
    initialNumToRender: DEFAULT_INITIAL_NUM_TO_RENDER,
    listContainerClassName: undefined,
};
